import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {add, deleteRecord, fetch, update} from '../services/SubscriptionItemsService';
import {SubscriptionItem} from '../types';
import {notifyError, notifySuccess} from '../../../components/ServiceNotifications';

export const useSubscriptionItem = (customerId: string, invoiceItemId?: string) => {

    if (typeof customerId === 'undefined') {
        throw new Error('customerId is required parameter')
    }

    const queryClient = useQueryClient();

    const subscriptionItemQuery = useQuery({
        queryKey: ['subscriptionItem', customerId, invoiceItemId],
        queryFn: () => fetch(customerId, invoiceItemId!),
        enabled: !!invoiceItemId,
    });

    const createMutation = useMutation({
        mutationFn: (values: SubscriptionItem) => {
            const modified = Object.assign({}, values, {customerId: customerId});
            return add(modified);
        },
        onSuccess: () => {
            notifySuccess('Subscription item created');
            queryClient.invalidateQueries({queryKey: ['subscriptionItems', customerId]});
        },
        onError: notifyError,
    });

    const updateMutation = useMutation({
        mutationFn: (values: SubscriptionItem) => {
            if (!invoiceItemId) throw new Error('invoiceItemId is required parameter');
            const modified = Object.assign({}, values, {id: invoiceItemId, customerId: customerId});
            return update(modified);
        },
        onSuccess: () => {
            notifySuccess('Subscription item updated');
            queryClient.invalidateQueries({queryKey: ['subscriptionItems', customerId]});
            queryClient.invalidateQueries({queryKey: ['subscriptionItem', customerId, invoiceItemId]});
        },
        onError: notifyError,
    });

    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!invoiceItemId) throw new Error('invoiceItemId is required parameter');
            return deleteRecord(customerId, invoiceItemId);
        },
        onSuccess: () => {
            notifySuccess('Subscription item deleted');
            queryClient.invalidateQueries({queryKey: ['subscriptionItems', customerId]});
        },
        onError: notifyError,
    });

    return {
        record: subscriptionItemQuery.data,
        loading: subscriptionItemQuery.isLoading,
        fetchRecord: subscriptionItemQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation,
    };
};
